<template>
  <div class="transition-query-card">
    <div class="query-header">
      <h6 class="query-title">
        <v-icon name="bi-search" scale="0.9" class="mr-2" />
        Consulta de Transiciones
      </h6>
      <span class="query-subtitle">Calcula tiempos entre estados específicos</span>
    </div>

    <div class="query-form">
      <div class="row">
        <div class="col-md-6 col-lg-3 mb-3">
          <label class="form-label">Estado Origen</label>
          <select v-model="fromStatus" class="form-select">
            <option value="">Seleccionar estado...</option>
            <option v-for="status in availableStatuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        <div class="col-md-6 col-lg-3 mb-3">
          <label class="form-label">Estado Destino</label>
          <select v-model="toStatus" class="form-select">
            <option value="">Cualquier estado siguiente</option>
            <option v-for="status in availableStatuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        <div class="col-md-6 col-lg-3 mb-3">
          <label class="form-label">Fecha Desde</label>
          <input
            type="date"
            v-model="dateFrom"
            class="form-control"
            :max="dateTo || today"
          />
        </div>
        <div class="col-md-6 col-lg-3 mb-3">
          <label class="form-label">Fecha Hasta</label>
          <input
            type="date"
            v-model="dateTo"
            class="form-control"
            :min="dateFrom"
            :max="today"
          />
        </div>
      </div>
      <div class="row align-items-end">
        <div class="col-md-8 mb-3">
          <div class="quick-periods">
            <span class="quick-label">Períodos rápidos:</span>
            <button
              v-for="p in quickPeriods"
              :key="p.value"
              class="btn-period"
              :class="{ active: isActivePeriod(p.value) }"
              @click="setQuickPeriod(p.value)"
            >
              {{ p.label }}
            </button>
          </div>
        </div>
        <div class="col-md-4 mb-3">
          <button
            class="btn-query w-100"
            @click="executeQuery"
            :disabled="!fromStatus || !dateFrom || !dateTo || loading"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
            <v-icon v-else name="bi-play-fill" scale="0.9" class="mr-2" />
            {{ loading ? loadingMessage : 'Ejecutar Consulta' }}
          </button>
        </div>
      </div>
      <!-- Barra de progreso -->
      <div class="progress-container" v-if="loading">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <span class="progress-text">{{ progressText }}</span>
      </div>
    </div>

    <!-- Resultados -->
    <div class="query-results" v-if="results">
      <div class="results-header">
        <h6 class="results-title">
          <v-icon name="bi-graph-up" scale="0.9" class="mr-2" />
          Resultados de la Consulta
        </h6>
        <span class="results-query-text">
          Tiempo de <strong>{{ formatStatus(fromStatus) }}</strong>
          <span v-if="toStatus"> → <strong>{{ formatStatus(toStatus) }}</strong></span>
          <span v-else> → siguiente estado</span>
          ({{ formatDateDisplay(dateFrom) }} - {{ formatDateDisplay(dateTo) }})
        </span>
      </div>

      <div class="results-metrics" v-if="results.totalContracts > 0">
        <!-- Métricas principales -->
        <div class="row">
          <div class="col-6 col-md-3 mb-3">
            <div class="metric-card">
              <span class="metric-value">{{ results.totalContracts }}</span>
              <span class="metric-label">Contratos analizados</span>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="metric-card highlight">
              <span class="metric-value">{{ formatDuration(results.avgTime) }}</span>
              <span class="metric-label">Tiempo medio general</span>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="metric-card fastest">
              <span class="metric-value">{{ formatDuration(results.minTime) }}</span>
              <span class="metric-label">Más rápido</span>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="metric-card slowest">
              <span class="metric-value">{{ formatDuration(results.maxTime) }}</span>
              <span class="metric-label">Más lento</span>
            </div>
          </div>
        </div>

        <!-- Métricas separadas: Ya transitaron vs Aún en estado -->
        <div class="row mt-2" v-if="results.contractsStillInState > 0 || results.contractsTransitioned > 0">
          <div class="col-12 mb-2">
            <h6 class="section-subtitle">Desglose por estado de transición</h6>
          </div>
          <div class="col-md-6 mb-3">
            <div class="metric-card-detailed transitioned">
              <div class="metric-header">
                <v-icon name="bi-check-circle" scale="0.9" class="mr-2" />
                Ya transitaron
              </div>
              <div class="metric-body">
                <span class="metric-count">{{ results.contractsTransitioned }}</span>
                <span class="metric-avg">
                  Tiempo medio: <strong>{{ formatDuration(results.avgTimeTransitioned) }}</strong>
                </span>
              </div>
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <div class="metric-card-detailed still-in-state">
              <div class="metric-header">
                <v-icon name="bi-hourglass-split" scale="0.9" class="mr-2" />
                Aún en "{{ formatStatus(fromStatus) }}"
              </div>
              <div class="metric-body">
                <span class="metric-count">{{ results.contractsStillInState }}</span>
                <span class="metric-avg">
                  Tiempo medio: <strong>{{ formatDuration(results.avgTimeStillInState) }}</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart de Tiempo Promedio por Día -->
        <div class="row mt-3" v-if="dailyChartLabels && dailyChartLabels.length > 0 && dailyChartData && dailyChartData.length > 0">
          <div class="col-12">
            <AreaChartGradient
              :key="'chart-' + dailyChartLabels.join('-')"
              :title="`Tiempo promedio por día en ${formatStatus(fromStatus)}`"
              :labels="dailyChartLabels"
              :data="dailyChartData"
              :legend-label="'Tiempo promedio (minutos)'"
              :summary="dailyChartSummary"
              :format-tooltip="formatChartTooltip"
              gradient-from="rgba(204, 11, 122, 0.4)"
              gradient-to="rgba(204, 11, 122, 0.02)"
              line-color="#CC0B7A"
            />
          </div>
        </div>

        <!-- Distribución de tiempos -->
        <div class="time-distribution" v-if="results.distribution.length > 0">
          <h6 class="distribution-title">Distribución de Tiempos</h6>
          <div class="distribution-bars">
            <div
              v-for="bucket in results.distribution"
              :key="bucket.label"
              class="distribution-item"
            >
              <div class="bar-container">
                <div
                  class="bar"
                  :style="{ height: bucket.percentage + '%' }"
                  :class="getBarClass(bucket.label)"
                ></div>
              </div>
              <span class="bar-label">{{ bucket.label }}</span>
              <span class="bar-count">{{ bucket.count }}</span>
            </div>
          </div>
        </div>

      </div>

      <div class="no-results" v-else>
        <v-icon name="bi-inbox" scale="2" class="text-muted" />
        <p>No se encontraron transiciones con los criterios seleccionados</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import AreaChartGradient from "./AreaChartGradient.vue";

export default {
  name: "StateTransitionQuery",
  components: {
    AreaChartGradient,
  },
  props: {
    contracts: {
      type: Array,
      default: () => [],
    },
    statusTranslations: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      fromStatus: "",
      toStatus: "",
      // Fechas personalizadas
      dateFrom: moment().subtract(7, "days").format("YYYY-MM-DD"),
      dateTo: moment().format("YYYY-MM-DD"),
      today: moment().format("YYYY-MM-DD"),
      // Estado de carga
      loading: false,
      loadingMessage: "Cargando...",
      progressPercent: 0,
      progressText: "",
      // Resultados
      results: null,
      showAll: false,
      maxVisible: 10,
      // Períodos rápidos
      quickPeriods: [
        { value: "week", label: "7 días" },
        { value: "2weeks", label: "15 días" },
        { value: "month", label: "1 mes" },
        { value: "quarter", label: "3 meses" },
      ],
      // Datos para el chart diario
      dailyChartLabels: [],
      dailyChartData: [],
      dailyChartSummary: null,
      // Estados cargados desde la API
      statusList: [],
      statusDescriptions: {},
      loadingStatuses: false,
    };
  },
  created() {
    this.loadStatusData();
  },
  computed: {
    availableStatuses() {
      // Si tenemos estados de la API, usarlos
      if (this.statusList.length > 0) {
        return this.statusList.map(status => ({
          value: status.name,
          label: status.statusDescription || this.statusTranslations[status.name] || status.name.replace(/_/g, " ")
        }));
      }
      // Fallback a estados por defecto
      const defaultStatuses = [
        "CREATED", "PENDING_VALIDATION", "PENDING_SCORING", "PENDING_PAYMENT",
        "PENDING_REVIEW_BO", "ACCEPTED", "ACTIVE", "PENDING_DELIVERY",
        "DELIVERED", "REJECTED", "CANCELLED", "FINISHED", "RENOVATED"
      ];
      return defaultStatuses.map(status => ({
        value: status,
        label: this.statusTranslations[status] || status.replace(/_/g, " ")
      }));
    },
    visibleContracts() {
      if (!this.results) return [];
      if (this.showAll) return this.results.contracts;
      return this.results.contracts.slice(0, this.maxVisible);
    },
    isBusinessUser() {
      return this.$store?.state?.roles?.Backoffice_member_empresas || false;
    },
  },
  methods: {
    // ============ CARGAR ESTADOS DESDE LA API ============
    async loadStatusData() {
      this.loadingStatuses = true;
      try {
        const url = this.isBusinessUser ? "/business/contract/status" : "/contract/status-list";
        const response = await axios.get(url);

        this.statusList = response.data || [];

        // Crear mapas bidireccionales para normalización de estados
        // statusDescriptions: almacena todas las variantes que apuntan al código inglés
        this.statusList.forEach((status) => {
          if (status.name && status.statusDescription) {
            const englishCode = status.name.toUpperCase();
            const spanishDesc = status.statusDescription.toUpperCase();

            // Guardar múltiples formas de acceder al mismo estado
            // Código inglés -> descripción española (original case)
            this.statusDescriptions[status.name] = status.statusDescription;
            // Código inglés uppercase -> descripción española
            this.statusDescriptions[englishCode] = status.statusDescription;
            // Descripción española uppercase -> código inglés
            this.statusDescriptions[spanishDesc] = status.name;
            // Descripción española original -> código inglés
            this.statusDescriptions[status.statusDescription] = status.name;
          }
        });

        console.log("[StateTransitionQuery] Estados cargados:", this.statusList.length);
        console.log("[StateTransitionQuery] Lista de estados:", this.statusList.map(s => `${s.name} -> ${s.statusDescription}`));
      } catch (error) {
        console.error("[StateTransitionQuery] Error cargando estados:", error);
      } finally {
        this.loadingStatuses = false;
      }
    },

    // ============ NORMALIZAR ESTADO A CÓDIGO INGLÉS ============
    // Dado cualquier representación de estado (inglés o español), devuelve el código inglés
    normalizeStatus(status) {
      if (!status) return "";

      const statusUpper = status.toUpperCase().trim();

      // Buscar en la lista de estados
      for (const s of this.statusList) {
        const englishCode = s.name.toUpperCase();
        const spanishDesc = s.statusDescription.toUpperCase();

        // Si coincide con el código inglés
        if (statusUpper === englishCode || statusUpper.includes(englishCode) || englishCode.includes(statusUpper)) {
          return s.name;
        }

        // Si coincide con la descripción española
        if (statusUpper === spanishDesc || statusUpper.includes(spanishDesc) || spanishDesc.includes(statusUpper)) {
          return s.name;
        }
      }

      // Si no se encontró, devolver el original en uppercase
      return status.toUpperCase();
    },

    // ============ HELPER: VERIFICAR SI DOS ESTADOS COINCIDEN ============
    // Normaliza ambos estados al código inglés y compara
    statusesMatch(status1, status2) {
      if (!status1 || !status2) return false;

      // Normalizar ambos estados
      const norm1 = this.normalizeStatus(status1);
      const norm2 = this.normalizeStatus(status2);

      // Comparación directa de normalizados
      if (norm1 === norm2) {
        return true;
      }

      // Comparación uppercase
      const upper1 = status1.toUpperCase().trim();
      const upper2 = status2.toUpperCase().trim();

      if (upper1 === upper2) {
        return true;
      }

      // Comparación con includes (para casos donde el historial tiene texto adicional)
      if (upper1.includes(upper2) || upper2.includes(upper1)) {
        return true;
      }

      // Comparar normalizados con includes
      if (norm1.includes(norm2) || norm2.includes(norm1)) {
        return true;
      }

      return false;
    },

    // Métodos para períodos rápidos
    setQuickPeriod(period) {
      const now = moment();
      this.dateTo = now.format("YYYY-MM-DD");
      switch (period) {
        case "week":
          this.dateFrom = now.clone().subtract(7, "days").format("YYYY-MM-DD");
          break;
        case "2weeks":
          this.dateFrom = now.clone().subtract(15, "days").format("YYYY-MM-DD");
          break;
        case "month":
          this.dateFrom = now.clone().subtract(1, "month").format("YYYY-MM-DD");
          break;
        case "quarter":
          this.dateFrom = now.clone().subtract(3, "months").format("YYYY-MM-DD");
          break;
      }
    },
    isActivePeriod(period) {
      const now = moment();
      const from = moment(this.dateFrom);
      const to = moment(this.dateTo);

      if (!to.isSame(now, "day")) return false;

      switch (period) {
        case "week":
          return from.isSame(now.clone().subtract(7, "days"), "day");
        case "2weeks":
          return from.isSame(now.clone().subtract(15, "days"), "day");
        case "month":
          return from.isSame(now.clone().subtract(1, "month"), "day");
        case "quarter":
          return from.isSame(now.clone().subtract(3, "months"), "day");
        default:
          return false;
      }
    },
    formatDateDisplay(date) {
      if (!date) return "";
      return moment(date).format("DD/MM/YYYY");
    },

    // ============ CONVERTIR FECHA YYYY-MM-DD A DD/MM/YYYY ============
    formatDateForApi(dateYYYYMMDD) {
      // El API espera formato YYYY-MM-DD, que es el que ya tenemos
      return dateYYYYMMDD;
    },

    // ============ EJECUTAR CONSULTA ============
    async executeQuery() {
      this.loading = true;
      this.results = null;
      this.progressPercent = 0;
      this.progressText = "Iniciando búsqueda...";
      this.loadingMessage = "Buscando contratos...";

      try {
        // 1. Obtener el total de contratos en el período
        this.progressText = "Obteniendo total de contratos...";
        const totalInfo = await this.getTotalContracts();
        const totalContracts = totalInfo.total;

        if (totalContracts === 0) {
          this.results = {
            totalContracts: 0,
            contractsTransitioned: 0,
            contractsStillInState: 0,
            avgTime: 0,
            avgTimeTransitioned: 0,
            avgTimeStillInState: 0,
            minTime: 0,
            maxTime: 0,
            distribution: [],
            contracts: [],
          };
          this.progressPercent = 100;
          this.progressText = "No se encontraron contratos";
          return;
        }

        this.progressText = `Encontrados ${totalContracts} contratos. Obteniendo datos...`;

        // 2. Obtener contratos en lotes y analizar
        const allContracts = await this.fetchAllContractsInPeriod(totalContracts);

        if (allContracts.length === 0) {
          this.results = {
            totalContracts: 0,
            contractsTransitioned: 0,
            contractsStillInState: 0,
            avgTime: 0,
            avgTimeTransitioned: 0,
            avgTimeStillInState: 0,
            minTime: 0,
            maxTime: 0,
            distribution: [],
            contracts: [],
          };
          this.progressPercent = 100;
          this.progressText = "No se encontraron contratos";
          return;
        }

        this.progressText = `Analizando historiales de ${allContracts.length} contratos...`;
        this.loadingMessage = "Analizando historiales...";

        // 3. Analizar transiciones
        const transitionData = await this.analyzeContractsTransitions(allContracts);

        this.results = transitionData;

        // 4. Calcular promedios diarios para el chart
        this.calculateDailyAverages(transitionData.contracts);

        this.progressPercent = 100;
        this.progressText = "Completado";
      } catch (error) {
        console.error("[StateTransitionQuery] Error ejecutando consulta:", error);
        this.$emit("error", "Error al ejecutar la consulta: " + error.message);
      } finally {
        this.loading = false;
      }
    },

    // ============ OBTENER TOTAL DE CONTRATOS ============
    async getTotalContracts() {
      const apiUrl = this.isBusinessUser ? "/business/contract/search" : "/contract/search";

      // Construir URL con query string igual que ContractsList.vue
      let queryString = this.buildQueryString(1, 1);

      try {
        const response = await axios.get(`${apiUrl}?${queryString}`);
        const pagination = response.data.pagination || {};
        const total = pagination.totalElements || 0;

        return {
          total: total,
          pages: Math.ceil(total / 100),
        };
      } catch (error) {
        console.error("[StateTransitionQuery] Error obteniendo total:", error);
        return { total: 0, pages: 0 };
      }
    },

    // ============ CONSTRUIR QUERY STRING (igual que ContractsList.vue) ============
    buildQueryString(page, pageSize) {
      let filters = "";

      // Paginación - exactamente como ContractsList.vue
      if (this.isBusinessUser) {
        filters = `pageSize=${pageSize}&page=${page}`;
      } else {
        filters = `pagination.pageSize=${pageSize}&pagination.page=${page}`;
      }

      // Filtros de fecha - usar formato YYYY-MM-DD
      if (this.dateFrom) {
        filters += `&dateFrom=${this.dateFrom}`;
      }
      if (this.dateTo) {
        filters += `&dateTo=${this.dateTo}`;
      }

      return filters;
    },

    // ============ OBTENER TODOS LOS CONTRATOS CON PAGINACIÓN ============
    async fetchAllContractsInPeriod(totalContracts) {
      const allContracts = [];
      const pageSize = 100; // Máximo por página
      const totalPages = Math.ceil(totalContracts / pageSize);
      const apiUrl = this.isBusinessUser ? "/business/contract/search" : "/contract/search";

      for (let page = 1; page <= totalPages; page++) {
        this.progressPercent = Math.round((page / totalPages) * 40);
        this.progressText = `Obteniendo contratos: página ${page} de ${totalPages}...`;

        try {
          const queryString = this.buildQueryString(page, pageSize);

          const response = await axios.get(`${apiUrl}?${queryString}`);
          const contracts = response.data.contracts || [];

          allContracts.push(...contracts);
        } catch (error) {
          console.warn(`[StateTransitionQuery] Error en página ${page}:`, error.message);
        }
      }

      return allContracts;
    },

    // ============ ANALIZAR TRANSICIONES DE CONTRATOS (CON PARALELISMO) ============
    async analyzeContractsTransitions(contracts) {
      const transitionTimes = [];
      const stillInStateTimes = [];
      const analyzedContracts = [];
      const contractsStillInState = [];

      const totalContracts = contracts.length;
      const BATCH_SIZE = 20; // Procesar 20 contratos en paralelo

      let errorsCount = 0;

      // DEBUG: Mostrar contratos que están en el estado buscado
      console.log(`[analyzeContractsTransitions] Total contratos: ${totalContracts}, buscando estado origen: "${this.fromStatus}"`);

      // DEBUG: Mostrar estructura de los primeros 3 contratos para ver cómo viene el status
      console.log("[analyzeContractsTransitions] Estructura de primeros 3 contratos:");
      contracts.slice(0, 3).forEach((c, idx) => {
        console.log(`  Contrato ${idx + 1}:`, {
          contractId: c.contractId,
          status: c.status,
          contractStatus: c.contractStatus,
          state: c.state,
          statusObj: typeof c.status === "object" ? c.status : "N/A",
        });
      });

      // Buscar específicamente los contratos R00426852, R00426851, R00425181
      const targetIds = ["R00426852", "R00426851", "R00425181"];
      const targetContracts = contracts.filter(c => targetIds.includes(c.contractId));
      if (targetContracts.length > 0) {
        console.log("[analyzeContractsTransitions] CONTRATOS OBJETIVO ENCONTRADOS:");
        targetContracts.forEach(c => {
          console.log(`  ${c.contractId}:`, {
            status: c.status,
            contractStatus: c.contractStatus,
            state: c.state,
            date: c.date,
            createdAt: c.createdAt,
          });
        });
      } else {
        console.log("[analyzeContractsTransitions] CONTRATOS OBJETIVO NO ENCONTRADOS EN LA LISTA");
      }

      // Contar contratos por estado actual para debugging
      const statusCount = {};
      contracts.forEach((c) => {
        let st = "";
        if (c.status) {
          if (typeof c.status === "object") {
            st = c.status.status || c.status.name || c.status.statusDescription || "OBJ_UNKNOWN";
          } else {
            st = c.status;
          }
        } else if (c.contractStatus) {
          st = typeof c.contractStatus === "object" ? (c.contractStatus.status || c.contractStatus.name) : c.contractStatus;
        } else if (c.state) {
          st = c.state;
        } else {
          st = "NO_STATUS_FIELD";
        }
        statusCount[st] = (statusCount[st] || 0) + 1;
      });
      console.log("[analyzeContractsTransitions] Contratos por estado:", statusCount);

      // Mostrar IDs de contratos en CREATED (usando statusesMatch que maneja español/inglés)
      const contractsInCreated = contracts.filter((c) => {
        let st = "";
        if (c.status) {
          if (typeof c.status === "object") {
            st = (c.status.status || c.status.name || c.status.statusDescription || "").toUpperCase();
          } else {
            st = c.status.toUpperCase();
          }
        } else if (c.state) {
          st = c.state.toUpperCase();
        }
        return this.statusesMatch("CREATED", st);
      });
      console.log(`[analyzeContractsTransitions] Contratos actualmente en CREATED: ${contractsInCreated.length}`, contractsInCreated.map(c => c.contractId || c.businessContractId));

      // Procesar en batches paralelos
      for (let i = 0; i < totalContracts; i += BATCH_SIZE) {
        const batch = contracts.slice(i, i + BATCH_SIZE);

        // Actualizar progreso
        this.progressPercent = 40 + Math.round((i / totalContracts) * 60);
        this.progressText = `Analizando historiales: ${Math.min(i + BATCH_SIZE, totalContracts)} de ${totalContracts}...`;

        // Ejecutar batch en paralelo
        const batchPromises = batch.map(async (contract) => {
          const contractId = contract.contractId || contract.businessContractId;

          // DEBUG específico para contratos objetivo
          const isTargetContract = ["R00426852", "R00426851", "R00425181"].includes(contractId);
          if (isTargetContract) {
            console.log(`[DEBUG TARGET] Procesando contrato ${contractId}:`);
            console.log(`  - contract.status: "${contract.status}"`);
            console.log(`  - contract.statusDescription: "${contract.statusDescription}"`);
            console.log(`  - this.fromStatus: "${this.fromStatus}"`);
            console.log(`  - statusesMatch(fromStatus, "CREATED"): ${this.statusesMatch(this.fromStatus, "CREATED")}`);
            console.log(`  - statusesMatch("CREATED", contract.status): ${this.statusesMatch("CREATED", contract.status)}`);
          }

          try {
            const history = await this.fetchContractHistory(contractId);

            if (isTargetContract) {
              console.log(`[DEBUG TARGET] ${contractId} - Historial tiene ${history ? history.length : 0} entradas`);
              if (history && history.length > 0) {
                console.log(`[DEBUG TARGET] ${contractId} - Historial:`, history.map(h => h.statusDescription));
              }
            }

            if (!history || history.length === 0) {
              // Sin historial - usar statusesMatch para comparación flexible
              if (this.statusesMatch(this.fromStatus, "CREATED")) {
                const result = this.analyzeContractWithoutHistory(contract);
                if (isTargetContract) {
                  console.log(`[DEBUG TARGET] ${contractId} - Resultado analyzeContractWithoutHistory:`, result);
                }
                return { type: "noHistory", result: result };
              }
              return { type: "noHistory", result: null };
            }

            const result = this.analyzeContractHistory(history, contract);
            if (isTargetContract) {
              console.log(`[DEBUG TARGET] ${contractId} - Resultado analyzeContractHistory:`, result);
            }
            return { type: "withHistory", result: result };
          } catch (error) {
            if (isTargetContract) {
              console.error(`[DEBUG TARGET] ${contractId} - Error:`, error);
            }
            return { type: "error", contractId, error: error.message };
          }
        });

        const batchResults = await Promise.all(batchPromises);

        // Procesar resultados del batch
        for (const item of batchResults) {
          if (item.type === "error") {
            errorsCount++;
            if (errorsCount <= 3) {
              console.warn(`[StateTransitionQuery] Error en ${item.contractId}:`, item.error);
            }
            continue;
          }

          const transitionInfo = item.result;
          if (transitionInfo) {
            // La lógica de filtrado por toStatus ahora está en analyzeContractHistory
            // Si devuelve un resultado, ya cumple con los criterios de búsqueda

            if (transitionInfo.stillInState) {
              stillInStateTimes.push(transitionInfo.timeInMinutes);
              contractsStillInState.push(transitionInfo);
            } else {
              transitionTimes.push(transitionInfo.timeInMinutes);
              analyzedContracts.push(transitionInfo);
            }
          }
        }
      }

      // Combinar todos los tiempos para estadísticas generales (en minutos)
      const allTimes = [...transitionTimes, ...stillInStateTimes];

      // Calcular estadísticas (en minutos)
      const avgTime = allTimes.length > 0
        ? allTimes.reduce((a, b) => a + b, 0) / allTimes.length
        : 0;
      const minTime = allTimes.length > 0 ? Math.min(...allTimes) : 0;
      const maxTime = allTimes.length > 0 ? Math.max(...allTimes) : 0;

      // Estadísticas solo de los que ya transitaron
      const avgTimeTransitioned = transitionTimes.length > 0
        ? transitionTimes.reduce((a, b) => a + b, 0) / transitionTimes.length
        : 0;

      // Estadísticas solo de los que aún están en el estado
      const avgTimeStillInState = stillInStateTimes.length > 0
        ? stillInStateTimes.reduce((a, b) => a + b, 0) / stillInStateTimes.length
        : 0;

      // Calcular distribución de todos
      const distribution = this.calculateDistribution(allTimes);

      // Combinar contratos, poniendo primero los que aún están en el estado
      const allContractsResult = [
        ...contractsStillInState.sort((a, b) => b.timeInMinutes - a.timeInMinutes),
        ...analyzedContracts.sort((a, b) => b.timeInMinutes - a.timeInMinutes),
      ];

      // Log final de resultados
      console.log(`[analyzeContractsTransitions] RESULTADO FINAL:`);
      console.log(`  - Total contratos encontrados: ${allContractsResult.length}`);
      console.log(`  - Ya transitaron: ${analyzedContracts.length}`);
      console.log(`  - Aún en estado: ${contractsStillInState.length}`);
      if (contractsStillInState.length > 0) {
        console.log(`  - Contratos aún en estado:`, contractsStillInState.map(c => `${c.contractId} (${(c.timeInMinutes/60).toFixed(1)}h)`));
      }

      return {
        totalContracts: allContractsResult.length,
        contractsTransitioned: analyzedContracts.length,
        contractsStillInState: contractsStillInState.length,
        avgTime: avgTime, // Ahora en minutos (no redondeado)
        avgTimeTransitioned: avgTimeTransitioned,
        avgTimeStillInState: avgTimeStillInState,
        minTime: minTime,
        maxTime: maxTime,
        distribution,
        contracts: allContractsResult,
      };
    },

    // ============ OBTENER HISTORIAL DE UN CONTRATO ============
    async fetchContractHistory(contractId) {
      // Endpoint: contract/history/{contractId} - igual que ContractDetail.vue
      const response = await axios.get(`contract/history/${contractId}`);
      return response.data.contractHistories || [];
    },

    // ============ ANALIZAR CONTRATO SIN HISTORIAL ============
    analyzeContractWithoutHistory(contract) {
      const contractId = contract.contractId || contract.businessContractId;

      // Extraer estado actual - puede ser string u objeto
      // La API puede devolver: status (string), status.status, status.name, status.statusDescription, state
      let currentStatus = "";
      if (contract.status) {
        if (typeof contract.status === "object") {
          currentStatus = (contract.status.status || contract.status.name || contract.status.statusDescription || "").toUpperCase();
        } else {
          currentStatus = contract.status.toUpperCase();
        }
      } else if (contract.contractStatus) {
        if (typeof contract.contractStatus === "object") {
          currentStatus = (contract.contractStatus.status || contract.contractStatus.name || contract.contractStatus.statusDescription || "").toUpperCase();
        } else {
          currentStatus = contract.contractStatus.toUpperCase();
        }
      } else if (contract.state) {
        currentStatus = contract.state.toUpperCase();
      }

      console.log(`[analyzeContractWithoutHistory] Contrato ${contractId} - Estado actual: "${currentStatus}", contract.status:`, contract.status, "contract.state:", contract.state);

      const creationDate = contract.date || contract.createdAt || contract.transDate;

      if (!creationDate) {
        return null;
      }

      const now = moment();
      const created = moment(creationDate);

      // Para CREATED: si no hay historial, el contrato nunca cambió de estado
      // Por lo tanto, si el estado actual es CREATED, aún está ahí
      if (this.statusesMatch(this.fromStatus, "CREATED")) {
        if (this.statusesMatch("CREATED", currentStatus)) {
          // Aún está en CREATED - calcular en SEGUNDOS para mayor precisión
          const timeInSeconds = now.diff(created, "seconds");
          const timeInMinutes = timeInSeconds / 60;
          console.log(`[analyzeContractWithoutHistory] Contrato ${contractId} - AÚN EN CREATED! Tiempo: ${timeInMinutes.toFixed(1)} minutos (${(timeInMinutes/60).toFixed(1)}h)`);
          return {
            contractId: contractId,
            transitionDate: creationDate,
            creationDate: creationDate, // Fecha de creación para agrupar en chart
            timeInSeconds: Math.max(0, timeInSeconds),
            timeInMinutes: Math.max(0, timeInMinutes),
            timeInHours: Math.max(0, timeInMinutes / 60), // Para compatibilidad
            user: "Sistema",
            fromStatus: "CREATED",
            toStatus: "(Aún en este estado)",
            stillInState: true,
          };
        } else {
          // Ya no está en CREATED pero no hay historial
          // No podemos saber cuánto tiempo estuvo, así que lo omitimos
          console.log(`[analyzeContractWithoutHistory] Contrato ${contractId} - No está en CREATED (está en ${currentStatus}), sin historial, omitiendo`);
          return null;
        }
      }

      return null;
    },

    // ============ ANALIZAR HISTORIAL DEL CONTRATO ============
    // El historial tiene: transChangeDate, userUpdatedId, statusDescription
    // statusDescription = ESTADO ANTERIOR (del que salió)
    //
    // NUEVA LÓGICA: Calcula tiempo desde estado origen hasta estado destino
    // (pasando por estados intermedios si los hay)
    analyzeContractHistory(history, contract) {
      const contractId = contract.contractId || contract.businessContractId;

      // Extraer estado actual - puede ser string u objeto
      let currentStatus = "";
      if (contract.status) {
        if (typeof contract.status === "object") {
          currentStatus = (contract.status.status || contract.status.name || contract.status.statusDescription || "").toUpperCase();
        } else {
          currentStatus = contract.status.toUpperCase();
        }
      } else if (contract.contractStatus) {
        if (typeof contract.contractStatus === "object") {
          currentStatus = (contract.contractStatus.status || contract.contractStatus.name || contract.contractStatus.statusDescription || "").toUpperCase();
        } else {
          currentStatus = contract.contractStatus.toUpperCase();
        }
      } else if (contract.state) {
        currentStatus = contract.state.toUpperCase();
      }

      const creationDate = contract.date || contract.createdAt || contract.transDate;
      const now = moment();

      // Ordenar historial por fecha (más antiguo primero)
      const sortedHistory = [...history].sort((a, b) =>
        moment(a.transChangeDate).valueOf() - moment(b.transChangeDate).valueOf()
      );

      // ========== PASO 1: ENCONTRAR FECHA DE SALIDA DEL ESTADO ORIGEN ==========
      let exitFromOriginDate = null;
      let exitFromOriginEntry = null;

      // Caso especial: CREATED - la fecha de entrada es la fecha de creación
      const isFromCreated = this.statusesMatch(this.fromStatus, "CREATED");

      if (isFromCreated) {
        // Para CREATED, la fecha de entrada es la fecha de creación del contrato
        if (!creationDate) {
          return null;
        }
        // Buscar cuándo salió de CREATED
        for (let i = 0; i < sortedHistory.length; i++) {
          const entry = sortedHistory[i];
          const estadoAnterior = (entry.statusDescription || "").toUpperCase();
          if (this.statusesMatch("CREATED", estadoAnterior)) {
            exitFromOriginDate = moment(entry.transChangeDate);
            exitFromOriginEntry = entry;
            break;
          }
        }
        // Si no encontramos salida de CREATED, el contrato aún está en CREATED
        if (!exitFromOriginDate) {
          if (this.statusesMatch("CREATED", currentStatus)) {
            const timeInSeconds = now.diff(moment(creationDate), "seconds");
            const timeInMinutes = timeInSeconds / 60;
            return {
              contractId: contractId,
              transitionDate: creationDate,
              creationDate: creationDate,
              timeInSeconds: Math.max(0, timeInSeconds),
              timeInMinutes: Math.max(0, timeInMinutes),
              timeInHours: Math.max(0, timeInMinutes / 60),
              user: "Pendiente",
              fromStatus: this.fromStatus,
              toStatus: "(Aún en este estado)",
              stillInState: true,
            };
          }
          return null;
        }
      } else {
        // Para otros estados, buscar cuándo salió del estado origen
        for (let i = 0; i < sortedHistory.length; i++) {
          const entry = sortedHistory[i];
          const estadoAnterior = entry.statusDescription || "";
          if (this.statusesMatch(this.fromStatus, estadoAnterior)) {
            exitFromOriginDate = moment(entry.transChangeDate);
            exitFromOriginEntry = entry;
            break;
          }
        }
        // Si no encontramos salida del estado origen
        if (!exitFromOriginDate) {
          // Verificar si el contrato está actualmente en el estado origen
          if (this.statusesMatch(this.fromStatus, currentStatus)) {
            // Calcular tiempo que lleva en este estado
            // Buscar cuándo entró a este estado
            let entryDate = moment(creationDate);
            for (let i = sortedHistory.length - 1; i >= 0; i--) {
              const entry = sortedHistory[i];
              // La entrada más reciente nos dice de qué estado salió para llegar al actual
              entryDate = moment(entry.transChangeDate);
              break;
            }
            const timeInSeconds = now.diff(entryDate, "seconds");
            const timeInMinutes = timeInSeconds / 60;
            return {
              contractId: contractId,
              transitionDate: entryDate.format(),
              creationDate: creationDate,
              timeInSeconds: Math.max(0, timeInSeconds),
              timeInMinutes: Math.max(0, timeInMinutes),
              timeInHours: Math.max(0, timeInMinutes / 60),
              user: "Pendiente",
              fromStatus: this.fromStatus,
              toStatus: "(Aún en este estado)",
              stillInState: true,
            };
          }
          return null;
        }
      }

      // ========== PASO 2: SI HAY ESTADO DESTINO, BUSCAR CUÁNDO LLEGÓ ==========
      if (this.toStatus) {
        // Buscar cuándo llegó al estado destino
        // El contrato "llegó" al estado destino cuando hay una entrada en el historial
        // que indica que SALIÓ de ese estado, O si el estado actual ES el destino

        let arrivalToDestDate = null;

        // Primero verificar si el estado actual es el destino
        if (this.statusesMatch(this.toStatus, currentStatus)) {
          // El contrato está actualmente en el estado destino
          // Buscar cuándo entró a este estado (la última entrada del historial)
          if (sortedHistory.length > 0) {
            arrivalToDestDate = moment(sortedHistory[sortedHistory.length - 1].transChangeDate);
          } else {
            arrivalToDestDate = now;
          }
        } else {
          // Buscar en el historial si pasó por el estado destino
          for (let i = 0; i < sortedHistory.length; i++) {
            const entry = sortedHistory[i];
            const estadoAnterior = (entry.statusDescription || "").toUpperCase();
            if (this.statusesMatch(this.toStatus, estadoAnterior)) {
              // Esta entrada indica que SALIÓ del estado destino
              // Necesitamos saber cuándo ENTRÓ, que es la entrada anterior
              if (i > 0) {
                arrivalToDestDate = moment(sortedHistory[i - 1].transChangeDate);
              } else {
                arrivalToDestDate = moment(creationDate);
              }
              break;
            }
          }
        }

        // Si no encontramos que llegó al estado destino, no incluir este contrato
        if (!arrivalToDestDate) {
          return null;
        }

        // Calcular tiempo desde que salió del origen hasta que llegó al destino
        const startDate = isFromCreated ? moment(creationDate) : exitFromOriginDate;
        const timeInSeconds = arrivalToDestDate.diff(startDate, "seconds");
        const timeInMinutes = timeInSeconds / 60;

        return {
          contractId: contractId,
          transitionDate: arrivalToDestDate.format(),
          creationDate: creationDate,
          timeInSeconds: Math.max(0, timeInSeconds),
          timeInMinutes: Math.max(0, timeInMinutes),
          timeInHours: Math.max(0, timeInMinutes / 60),
          user: exitFromOriginEntry ? exitFromOriginEntry.userUpdatedId || "Sistema" : "Sistema",
          fromStatus: this.fromStatus,
          toStatus: this.toStatus,
          stillInState: false,
        };
      }

      // ========== PASO 3: SIN ESTADO DESTINO - CALCULAR TIEMPO EN ESTADO ORIGEN ==========
      // Calcular tiempo que estuvo en el estado origen (hasta que salió)
      const startDate = isFromCreated ? moment(creationDate) : (() => {
        // Buscar cuándo entró al estado origen
        const exitIndex = sortedHistory.findIndex(e =>
          this.statusesMatch(this.fromStatus, e.statusDescription || "")
        );
        if (exitIndex > 0) {
          return moment(sortedHistory[exitIndex - 1].transChangeDate);
        }
        return moment(creationDate);
      })();

      const timeInSeconds = exitFromOriginDate.diff(startDate, "seconds");
      const timeInMinutes = timeInSeconds / 60;

      // Determinar el estado al que fue después
      const exitIndex = sortedHistory.findIndex(e =>
        this.statusesMatch(this.fromStatus, e.statusDescription || "")
      );
      let destinationStatus = currentStatus;
      if (exitIndex >= 0 && exitIndex + 1 < sortedHistory.length) {
        destinationStatus = sortedHistory[exitIndex + 1].statusDescription || currentStatus;
      }

      return {
        contractId: contractId,
        transitionDate: exitFromOriginDate.format(),
        creationDate: creationDate,
        timeInSeconds: Math.max(0, timeInSeconds),
        timeInMinutes: Math.max(0, timeInMinutes),
        timeInHours: Math.max(0, timeInMinutes / 60),
        user: exitFromOriginEntry ? exitFromOriginEntry.userUpdatedId || "Sistema" : "Sistema",
        fromStatus: this.fromStatus,
        toStatus: destinationStatus,
        stillInState: false,
      };
    },

    // Recibe tiempos en MINUTOS
    calculateDistribution(times) {
      if (times.length === 0) return [];

      // Buckets en minutos
      const buckets = [
        { label: "< 30m", min: 0, max: 30, count: 0 },
        { label: "30m-1h", min: 30, max: 60, count: 0 },
        { label: "1-6h", min: 60, max: 360, count: 0 },
        { label: "6-24h", min: 360, max: 1440, count: 0 },
        { label: "1-2d", min: 1440, max: 2880, count: 0 },
        { label: "2-5d", min: 2880, max: 7200, count: 0 },
        { label: "> 5d", min: 7200, max: Infinity, count: 0 },
      ];

      times.forEach((time) => {
        const bucket = buckets.find((b) => time >= b.min && time < b.max);
        if (bucket) bucket.count++;
      });

      const maxCount = Math.max(...buckets.map((b) => b.count), 1);
      return buckets.map((b) => ({
        label: b.label,
        count: b.count,
        percentage: (b.count / maxCount) * 100,
      }));
    },

    formatStatus(status) {
      if (!status) return "Desconocido";

      // Buscar en los estados disponibles
      const statusObj = this.availableStatuses.find((s) =>
        s.value === status || s.value.toUpperCase() === status.toUpperCase()
      );
      if (statusObj) return statusObj.label;

      // Buscar en las descripciones cargadas de la API
      if (this.statusDescriptions[status]) {
        return this.statusDescriptions[status];
      }
      if (this.statusDescriptions[status.toUpperCase()]) {
        return this.statusDescriptions[status.toUpperCase()];
      }

      // Buscar en las traducciones pasadas como prop
      if (this.statusTranslations[status]) {
        return this.statusTranslations[status];
      }
      if (this.statusTranslations[status.toUpperCase()]) {
        return this.statusTranslations[status.toUpperCase()];
      }

      // Formatear el string si no se encontró traducción
      return status.replace(/_/g, " ");
    },

    // Recibe minutos y muestra formato legible
    formatDuration(minutes) {
      if (minutes === 0 || minutes === undefined || minutes === null) return "-";

      // Menos de 1 hora: mostrar minutos exactos
      if (minutes < 60) {
        return `${Math.round(minutes)} min`;
      }

      // Menos de 24 horas: mostrar horas y minutos
      if (minutes < 1440) {
        const hours = Math.floor(minutes / 60);
        const remainingMins = Math.round(minutes % 60);
        if (remainingMins === 0) return `${hours}h`;
        return `${hours}h ${remainingMins}m`;
      }

      // 24 horas o más: mostrar días y horas
      const days = Math.floor(minutes / 1440);
      const remainingHours = Math.round((minutes % 1440) / 60);
      if (remainingHours === 0) return `${days} días`;
      return `${days}d ${remainingHours}h`;
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },

    // Recibe minutos
    getTimeClass(minutes) {
      if (minutes > 7200) return "time-critical"; // > 5 días
      if (minutes > 2880) return "time-warning";  // > 2 días
      return "";
    },

    getBarClass(label) {
      if (label.includes("< 30") || label.includes("30m-1h")) return "bar-fast";
      if (label.includes("> 5")) return "bar-slow";
      return "bar-normal";
    },

    toggleShowAll() {
      this.showAll = !this.showAll;
    },

    // ============ CALCULAR PROMEDIOS DIARIOS PARA EL CHART ============
    calculateDailyAverages(contracts) {
      // Siempre generar los días del rango, incluso sin contratos
      const labels = [];
      const data = [];
      const counts = [];

      // Generar todos los días en el rango seleccionado
      const startDate = moment(this.dateFrom);
      const endDate = moment(this.dateTo);
      const currentDate = startDate.clone();
      const dailyData = {};

      while (currentDate.isSameOrBefore(endDate, "day")) {
        const dayKey = currentDate.format("YYYY-MM-DD");
        dailyData[dayKey] = [];
        currentDate.add(1, "day");
      }

      if (!contracts || contracts.length === 0) {
        Object.keys(dailyData).sort().forEach((dayKey) => {
          const dayMoment = moment(dayKey);
          labels.push(dayMoment.format("ddd DD"));
          data.push(0);
          counts.push(0);
        });

        this.dailyChartLabels = labels;
        this.dailyChartData = data;
        this.dailyChartSummary = null;
        return;
      }

      // Calcular el tiempo promedio general de todos los contratos
      // Usar timeInSeconds si está disponible para mayor precisión
      const allTimesInSeconds = contracts
        .map(c => c.timeInSeconds !== undefined ? c.timeInSeconds : (c.timeInMinutes || 0) * 60)
        .filter(t => t !== undefined && t !== null);

      const avgTimeInSeconds = allTimesInSeconds.length > 0
        ? allTimesInSeconds.reduce((a, b) => a + b, 0) / allTimesInSeconds.length
        : 0;

      // Distribuir contratos por día usando la fecha de transición
      // La fecha de transición indica cuándo el contrato SALIÓ del estado
      // Usar segundos para mayor precisión
      contracts.forEach((contract) => {
        // Usar transitionDate que es cuando ocurrió la transición
        const transDate = contract.transitionDate;
        if (!transDate) return;

        const dayKey = moment(transDate).format("YYYY-MM-DD");
        // Usar segundos si está disponible
        const timeValue = contract.timeInSeconds !== undefined ? contract.timeInSeconds : (contract.timeInMinutes || 0) * 60;

        // Si está en el rango, agregar
        if (Object.prototype.hasOwnProperty.call(dailyData, dayKey)) {
          dailyData[dayKey].push(timeValue); // Ahora guarda segundos
        }
        // Si no está en el rango, no lo agregamos
      });

      // Calcular promedio por día (en segundos)
      // Si un día no tiene contratos, mostrar el promedio general
      const dailyAvgSeconds = [];

      Object.keys(dailyData)
        .sort()
        .forEach((dayKey) => {
          const times = dailyData[dayKey]; // Array de segundos
          const dayMoment = moment(dayKey);

          // Formato corto para el label: "Mar 02"
          labels.push(dayMoment.format("ddd DD"));
          counts.push(times.length);

          if (times.length > 0) {
            const avgSeconds = times.reduce((a, b) => a + b, 0) / times.length;
            dailyAvgSeconds.push(avgSeconds);
            data.push(Math.round(avgSeconds)); // Guardar en segundos
          } else {
            // Día sin transiciones: mostrar promedio general en segundos
            dailyAvgSeconds.push(avgTimeInSeconds);
            data.push(Math.round(avgTimeInSeconds));
          }
        });

      this.dailyChartLabels = labels;
      this.dailyChartData = data; // Ahora en segundos

      // Calcular resumen
      const minSeconds = allTimesInSeconds.length > 0 ? Math.min(...allTimesInSeconds) : 0;
      const maxSeconds = allTimesInSeconds.length > 0 ? Math.max(...allTimesInSeconds) : 0;

      this.dailyChartSummary = {
        avg: this.formatDurationSeconds(avgTimeInSeconds),
        min: this.formatDurationSeconds(minSeconds),
        max: this.formatDurationSeconds(maxSeconds),
      };
    },

    // Formateador para segundos (usado en el chart y resumen)
    formatDurationSeconds(seconds) {
      if (seconds === 0 || seconds === undefined || seconds === null) return "-";

      // Menos de 60 segundos: mostrar segundos
      if (seconds < 60) {
        return `${Math.round(seconds)} seg`;
      }

      // Menos de 60 minutos: mostrar minutos y segundos
      if (seconds < 3600) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        if (secs === 0) return `${mins} min`;
        return `${mins}m ${secs}s`;
      }

      // Menos de 24 horas: mostrar horas y minutos
      if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.round((seconds % 3600) / 60);
        if (mins === 0) return `${hours}h`;
        return `${hours}h ${mins}m`;
      }

      // Más de 24 horas: mostrar días y horas
      const days = Math.floor(seconds / 86400);
      const hours = Math.round((seconds % 86400) / 3600);
      if (hours === 0) return `${days} días`;
      return `${days}d ${hours}h`;
    },

    // Formateador para tooltips del chart (recibe segundos)
    formatChartTooltip(value) {
      return this.formatDurationSeconds(value);
    },

    exportResults() {
      if (!this.results || !this.results.contracts.length) return;

      const headers = ["Contrato", "Estado Transición", "Fecha Cambio", "Tiempo (horas)", "Usuario", "Estado Origen", "Estado Destino"];
      const rows = this.results.contracts.map((c) => [
        c.contractId,
        c.stillInState ? "Pendiente" : "Transitó",
        this.formatDate(c.transitionDate),
        c.timeInHours,
        c.user || "Sistema",
        c.fromStatus,
        c.toStatus,
      ]);

      const csv = [headers.join(","), ...rows.map((r) => r.map((v) => `"${v}"`).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `transiciones_${this.fromStatus}_${this.dateFrom}_${this.dateTo}.csv`;
      link.click();
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../../scss/variables.scss';

.transition-query-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.query-header {
  margin-bottom: 1.25rem;
}

.query-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
  display: flex;
  align-items: center;
}

.query-subtitle {
  font-size: 0.8125rem;
  color: #6b7280;
}

.query-form {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.25rem;
}

.form-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
  display: block;
}

.form-select,
.form-control {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8125rem;
  background: #ffffff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }
}

.quick-periods {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.quick-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-right: 0.25rem;
}

.btn-period {
  padding: 0.375rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
  font-size: 0.75rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $primary;
    color: $primary;
  }

  &.active {
    background: $primary;
    border-color: $primary;
    color: #ffffff;
  }
}

.progress-container {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.progress-bar {
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, $primary 0%, darken($primary, 10%) 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
}

.btn-query {
  background: linear-gradient(135deg, $primary 0%, darken($primary, 10%) 100%);
  color: #ffffff;
  border: none;
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba($primary, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.query-results {
  border-top: 1px solid #f3f4f6;
  padding-top: 1.25rem;
}

.results-header {
  margin-bottom: 1rem;
}

.results-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
  display: flex;
  align-items: center;
}

.results-query-text {
  font-size: 0.8125rem;
  color: #6b7280;
}

.results-metrics {
  margin-bottom: 1.5rem;
}

.metric-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;

  &.highlight {
    background: linear-gradient(135deg, rgba($primary, 0.1) 0%, rgba($primary, 0.05) 100%);
    border: 1px solid rgba($primary, 0.2);
  }

  &.fastest {
    background: linear-gradient(135deg, rgba(#10b981, 0.1) 0%, rgba(#10b981, 0.05) 100%);
    border: 1px solid rgba(#10b981, 0.2);

    .metric-value {
      color: #059669;
    }
  }

  &.slowest {
    background: linear-gradient(135deg, rgba(#ef4444, 0.1) 0%, rgba(#ef4444, 0.05) 100%);
    border: 1px solid rgba(#ef4444, 0.2);

    .metric-value {
      color: #dc2626;
    }
  }
}

.metric-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.metric-label {
  display: block;
  font-size: 0.6875rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;
}

.time-distribution {
  margin-bottom: 1.5rem;
}

.distribution-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
}

.distribution-bars {
  display: flex;
  gap: 0.5rem;
  height: 120px;
  align-items: flex-end;
}

.distribution-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.bar-container {
  height: 80px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.bar {
  width: 80%;
  max-width: 40px;
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;

  &.bar-fast {
    background: linear-gradient(180deg, #10b981 0%, #059669 100%);
  }

  &.bar-normal {
    background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
  }

  &.bar-slow {
    background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
  }
}

.bar-label {
  font-size: 0.625rem;
  color: #6b7280;
  margin-top: 0.375rem;
}

.bar-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
}

.contracts-list {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.list-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.btn-export-sm {
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #ffffff;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
    color: $primary;
  }
}

.table-responsive {
  overflow-x: auto;
}

.table-contracts {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 0.625rem 0.5rem;
    text-align: left;
    font-size: 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }

  th {
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #ffffff;
  }

  td {
    color: #374151;
    background: #ffffff;
  }

  tbody tr:hover td {
    background: #f9fafb;
  }
}

.cell-id {
  font-weight: 600;
  color: $primary;
}

.time-warning {
  color: #d97706;
  font-weight: 500;
}

.time-critical {
  color: #dc2626;
  font-weight: 600;
}

.show-more {
  text-align: center;
  margin-top: 0.75rem;
}

.btn-show-more {
  background: transparent;
  border: none;
  color: $primary;
  font-size: 0.8125rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;

  &:hover {
    text-decoration: underline;
  }
}

.no-results {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;

  p {
    margin-top: 0.75rem;
    font-size: 0.875rem;
  }
}

// Estilos para desglose por estado de transición
.section-subtitle {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed #e5e7eb;
}

.metric-card-detailed {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  &.transitioned {
    border-left: 4px solid #10b981;
    background: linear-gradient(90deg, rgba(#10b981, 0.05) 0%, #ffffff 20%);

    .metric-header {
      color: #059669;
    }

    .metric-count {
      color: #10b981;
    }
  }

  &.still-in-state {
    border-left: 4px solid #f59e0b;
    background: linear-gradient(90deg, rgba(#f59e0b, 0.05) 0%, #ffffff 20%);

    .metric-header {
      color: #b45309;
    }

    .metric-count {
      color: #f59e0b;
    }
  }
}

.metric-header {
  font-size: 0.8125rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.metric-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.metric-count {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.metric-avg {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: right;

  strong {
    display: block;
    font-size: 0.875rem;
    color: #374151;
    margin-top: 0.125rem;
  }
}

// Estilos para badges de estado en la tabla
.status-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &.badge-transitioned {
    background: #d1fae5;
    color: #065f46;
  }

  &.badge-pending {
    background: #fef3c7;
    color: #92400e;
  }
}

// Resaltado para filas de contratos aún en estado
.row-still-in-state {
  td {
    background: #fffbeb !important;
  }

  &:hover td {
    background: #fef3c7 !important;
  }
}

@media (max-width: 768px) {
  .distribution-bars {
    height: 100px;
  }

  .bar-container {
    height: 60px;
  }

  .metric-body {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .metric-avg {
    text-align: left;
  }
}
</style>
